<template>
  <v-app
    :style="uiBackgroundUrl ? {
      backgroundImage: 'url(' + uiBackgroundUrl + ')',
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed'
    } : {}"
    :class="{
      'first-prestart-bg': !teaseStarted,
      'first-menu-bg': teaseStarted && pageId !== 'startspecialscreen' && isFirstMenuPage(pageId),
      'first-gameplay-full-image': teaseStarted && gameplayStarted && !isFirstMenuPage(pageId),
      'first-special-start-screen': pageId === 'startspecialscreen',
      'has-ui-background': !!uiBackgroundUrl
    }"
  >
    <div
      v-if="uiBackgroundUrl"
      class="tease-ui-fixed-bg"
      :style="{ backgroundImage: 'url(' + uiBackgroundUrl + ')' }"
    ></div>
    <v-main ref="mainPlayer">
      <open-eos-player
        v-if="script"
        :script="script"
        :title="title"
        :author="author"
        :author-id="authorId"
        :tease-id="teaseId"
        :tease-key="teaseKey"
        :is-fullscreen="this.isFullscreen"
        :tease-storage="teaseStorage"
        :debug-enabled="debugEnabled"
        :tease-url="teaseUrl"
        :debug-prompt="debugPrompt"
        :allow-no-sleep="allowNoSleep"
        :preview-mode="previewMode"
        :is-debug="previewMode > 0"
        @page-change="pageChange"
        @save-storage="didStorageSave"
        @load-storage="didStorageLoad"
        @tease-start="didTeaseStart"
        @tease-end="didTeaseEnd"
        @set-external-link="setExternalLink"
        @debugprompt="
          v => {
            debugPrompt = v
          }
        "
      />
      <v-container v-else>
        <loading v-if="loading">Loading MyTease...</loading>
        <v-alert v-else-if="fileError" type="error" prominent>
          {{ fileError }}
        </v-alert>
      </v-container>
    </v-main>
    <v-dialog v-model="downloadDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">
          Download Tease
        </v-card-title>

        <v-card-text>
          This will attempt to download <i>{{ displayTitle }}</i> for offline
          use. This operation could crash your browser if you don't have enough
          ram.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="green darken-1" text @click="downloadDialog = false">
            No thanks
          </v-btn>

          <v-btn color="green darken-1" text @click="startDownload">
            Do it!
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="message.show" max-width="290">
      <v-card>
        <v-card-title class="headline">
          {{ message.title }}
        </v-card-title>

        <v-card-text v-html="message.html"> </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            v-if="message.onCancel"
            color="green darken-1"
            text
            @click="
              message.onCancel()
              closeMessage()
            "
          >
            Nope
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="
              message.onContinue()
              closeMessage()
            "
          >
            Okay
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="downloading" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Downloading ({{ downloadedDisplay }})
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <input type="file" ref="teaseStorageLoader" style="display:none" />
  </v-app>
</template>

<script>
import OpenEosPlayer from './components/OpenEosPlayer'
import Loading from './components/common/Loading'
import { version } from '../package.json'
import {
  downloadObjectAsJson,
  downloadEosFile,
  convertToValidFilename,
  encodeForCorsProxy,
  corsProxyHeaders,
  FIX_POLLUTION,
  getFormattedDateForFile,
  acronym,
} from './util/io'
import prettysize from 'prettysize'
import CryptoJS from 'crypto-js'

const parser = new DOMParser()

export default {
  name: 'App',
  components: {
    OpenEosPlayer,
    Loading,
  },
  computed: {
    uiBackgroundUrl() {
      if (!this.teaseStarted) return '/images/ui/welcome.jpg'
      if (this.pageId === 'startspecialscreen') return '/images/ui/warning.jpg'
      if (this.isFirstMenuPage(this.pageId)) return '/images/ui/menu.jpg'
      return null
    },
    downloadedDisplay() {
      return prettysize(this.downloaded)
    },
    errors() {
      return this.error ? [this.error] : []
    },
    fileErrors() {
      return this.fileError ? [this.fileError] : []
    },
    displayTitle() {
      return this.title ? this.title : 'this tease'
    },
    debugEnabled() {
      return !this.formUri || !!this.previewMode
    },
  },
  data: () => ({
    isFullscreen: false,
    script: null,
    eosuri: null,
    error: null,
    fileError: null,
    title: null,
    author: null,
    authorId: null,
    teaseId: null,
    teaseKey: null,
    teaseUrl: null,
    formUri: false,
    previewMode: 0,
    loading: false,
    version: version,
    downloadDialog: false,
    downloading: false,
    fileUpload: null,
    externalLink: null,
    pageId: null,
    downloaded: 0,
    hasStorage: false,
    teaseStarted: false,
    gameplayStarted: false,
    teaseStorage: null,
    debugPrompt: false,
    allowNoSleep: false,
    message: {
      title: null,
      html: null,
      show: false,
    },
  }),
  mounted() {
    // Preload the three UI backgrounds immediately so menu/warning never appear late.
    ;['/images/ui/welcome.jpg', '/images/ui/menu.jpg', '/images/ui/warning.jpg'].forEach(src => {
      const img = new Image()
      img.src = src
    })
    const params = new URLSearchParams(window.location.search)
    const teaseId = params.get('id')

    if (teaseId) {
      this.formUri = true
      let teaseUrl = `https://milovana.com/webteases/showtease.php?id=${teaseId}`
      const key = params.get('key')
      if (key) teaseUrl += `&key=${key}`
      this.teaseUrl = teaseUrl
      this.loadMilovanaUrl()
    } else {
      this.loading = true
      this.fileError = null
      fetch(`/tease.json?v=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      })
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          return response.json()
        })
        .then(script => {
          if (!script || !script.pages) throw new Error('Invalid tease.json')
          const meta = script.oeosmeta || {}
          this.title = "RED LIGHT GREEN LIGHT (Feet Humiliation SPH)"
          this.author = ""
          this.authorId = meta.authorId || null
          this.teaseId = meta.teaseId || 'mytease-private'
          this.teaseKey = meta.teaseKey || null
          this.script = script
        })
        .catch(error => {
          console.error('AUTOLOAD failed:', error)
          this.fileError = `Could not auto-load /tease.json: ${error.message}`
        })
        .finally(() => { this.loading = false })
    }
  },

  methods: {
    setExternalLink(link) {
      this.externalLink = link || null
    },
    openExternalLink() {
      if (!this.externalLink || !this.externalLink.link) return
      window.open(this.externalLink.link, '_blank').focus()
    },
    restoreTeaseStorage() {
      const input = this.$refs.teaseStorageLoader
      input.type = 'file'
      input.accept = `.oeos_${this.teaseId}`
      input.addEventListener('change', handleFiles, false)
      const vue = this
      function handleFiles() {
        const file = this.files[0]
        if (file) {
          var reader = new FileReader()
          reader.onload = function(e) {
            try {
              const bytes = CryptoJS.AES.decrypt(
                JSON.parse(e.target.result),
                vue.teaseId + '::OEOS'
              )
              const storageData = bytes.toString(CryptoJS.enc.Utf8)
              if (
                storageData &&
                storageData.startsWith('{') &&
                JSON.parse(storageData)
              ) {
                vue.teaseStorage = storageData
              } else {
                console.error('Invalid data?', bytes, storageData)
                throw new Error('Invalid tease storage file', storageData)
              }
            } catch (err) {
              vue.showMessage(
                'Invalid Tease Storage',
                'Sorry, that file does not appear to be a valid storage file for this tease.'
              )
              console.error(err)
            }
          }
          reader.readAsText(file)
        }
      }
      input.click()
    },
    downloadTeaseStorage() {
      const storageData = CryptoJS.AES.encrypt(
        this.teaseStorage,
        this.teaseId + '::OEOS'
      ).toString()
      downloadObjectAsJson(
        storageData,
        convertToValidFilename(
          acronym(this.title) + '-' + getFormattedDateForFile()
        ),
        `oeos_${this.teaseId}`
      )
    },
    didStorageSave(v) {
      if (v && v !== '{}') {
        this.hasStorage = true
      }
      if (v) this.teaseStorage = v
    },
    didStorageLoad(v) {
      if (v && v !== '{}') {
        this.hasStorage = true
      } else if (!v) {
        this.hasStorage = false
      } else {
        this.hasStorage = null
      }
      console.log('Did storage load', v)
      if (v) this.teaseStorage = v
    },
    didTeaseStart() {
      this.teaseStarted = true
    },
    didTeaseEnd() {},
    isFirstMenuPage(pageId) {
      const menuPages = [
        'menupersonalization', 'cbtcustomization', 'cbtdifficulty',
        'changeftpicspercentage', 'changeftpicspercentage2', 'changedirpicspercentage',
        'changebootspicspercentage', 'changesockspicspercentage',
        'stroketypecustomization', 'twofingerstrokechance', 'onefingerstrokechance',
        'justtipstrokechance', 'swalloweventchance', 'lightpercentage',
        'cbtslapsettings', 'cbtslappercentage', 'cbtslaprange',
        'cbtspecialevents', 'cbtspecialeventspercentage', 'iwannasufferwarning'
      ]
      return menuPages.includes(pageId)
    },
    pageChange(pageId) {
      this.pageId = pageId
      if (this.isFirstMenuPage(pageId)) {
        this.gameplayStarted = false
      }
      if (
        pageId === 'greenlight' ||
        pageId === 'redlight' ||
        pageId === 'greenlight2' ||
        pageId === 'redlight2' ||
        pageId === 'special_2pair2' ||
        pageId === 'special_2pair3' ||
        pageId === 'special_2pair4'
      ) {
        this.gameplayStarted = true
      }
    },
    closeMessage() {
      this.message.show = false
      this.message.onContinue = () => {}
      this.message.onCancel = false
    },
    showMessage(title, html, onContinue, onCancel) {
      this.message.title = title
      this.message.html = html
      this.message.onContinue = onContinue || function() {}
      this.message.onCancel = onCancel
      this.message.show = true
    },
    async uploadFile() {
      console.log('Ready to upload', this.fileUpload)
      try {
        const script = JSON.parse(await this.fileUpload.text())
        if (
          !script ||
          !script.pages ||
          (script.modules && script.modules.nyx)
        ) {
          if (script.modules && script.modules.nyx) {
            this.fileError = 'Sorry, NYX teases are not supported.'
          } else {
            this.fileError =
              'Does not appear to be a valid EOS tease (Invalid Definitions)'
          }
        } else {
          if (script.oeosmeta) {
            const meta = script.oeosmeta
            this.title = meta.title
            this.author = meta.author
            this.authorId = meta.authorId
            this.teaseId = meta.teaseId
            this.teaseKey = meta.teaseKey
            this.script = script
          } else {
            this.message.title = 'Warning'
            this.message.html = `This appears to be a raw EOS file, not an Open EOS file.<br>
            Images, etc., will still be loaded from Milovana, and I won't know the title or author of this tease.<br>
            <br>
            Do you want to continue?`
            this.message.onContinue = () => {
              this.title = this.fileUpload.name
              this.script = script
              this.author = 'Unknown'
            }
            this.message.onCancel = () => {}
            this.message.show = true
          }
        }
      } catch (e) {
        this.fileError = 'Invalid JSON ffile'
      }
    },
    async downloadTease() {
      this.downloaded = 0
      const script = this.script
      if (script.galleries) {
        for (const gallery of Object.values(script.galleries)) {
          console.log('Downloading gallery', gallery)
          for (const image of gallery.images) {
            console.log('Downloading gallery image', image)
            this.downloaded += await downloadEosFile(image, true)
          }
        }
      }
      if (script.files) {
        for (const file of Object.values(script.files)) {
          console.log('Downloading file', file)
          this.downloaded += await downloadEosFile(file, true)
        }
      }
      script.oeosmeta = {
        title: this.title,
        author: this.author,
        authorId: this.authorId,
        teaseId: this.teaseId,
        teaseKey: this.teaseKey,
      }
      downloadObjectAsJson(
        script,
        convertToValidFilename(this.title || 'unnamed_tease') + '.oeos.json'
      )
    },
    startDownload() {
      this.downloadDialog = false
      this.downloading = true
      this.downloadTease()
        .then(() => {
          this.downloading = false
        })
        .catch(() => {
          this.downloading = false
          this.message.title = 'Error'
          this.message.html = 'Unable to download tease for unknown reason.'
          this.message.show = true
        })
    },
    loadMilovanaUrl() {
      this.error = null
      this.hasStorage = false
      this.easeStarted = false
      if (this.loading) return
      const uri = this.parseTeaseURI()
      if (!uri) {
        this.error = 'Invalid tease URL'
      } else {
        // this.getRemoteScriptName(uri)
        this.getRemoteScript(uri)
      }
    },
    toggleFullscreen() {
      this.isFullscreen = true
      requestAnimationFrame(() => this.$refs.mainPlayer.$el.requestFullscreen())
    },
    parseTeaseURI() {
      var teaseId = (this.teaseUrl || '').match(/id=([0-9a-z]+.*)/i)
      return (teaseId && teaseId[1]) || ''
    },
    getRemoteScript(uri) {
      this.loading = true
      fetch(
        encodeForCorsProxy(
          'https://milovana.com/webteases/geteosscript.php',
          `id=${uri}&${FIX_POLLUTION}` +
            (this.previewMode
              ? '&ncpreview=' + this.previewMode
              : '&cacheable&_nc=' + Math.floor(Date.now() / 1000 / 60))
        ),
        { headers: corsProxyHeaders() }
      )
        .then(response => response.json())
        .then(script => {
          if (
            !script ||
            !script.pages ||
            (script.modules && script.modules.nyx)
          ) {
            if (script.modules && script.modules.nyx) {
              this.error = 'Sorry, NYX teases are not supported.'
            } else {
              this.error =
                'Does not appear to be a valid EOS tease (Invalid Definitions)'
            }

            this.loading = false
          } else {
            this.getRemoteScriptName(uri, script)
          }
        })
        .catch(e => {
          fetch(
            encodeForCorsProxy(
              `https://milovana.com/webteases/showtease.php`,
              `&id=${uri}&${FIX_POLLUTION}` +
                (this.previewMode
                  ? '&preview=' + this.previewMode
                  : '&cacheable&_nc=' + Math.floor(Date.now() / 1000 / 60))
            ),
            { headers: corsProxyHeaders() }
          )
            .then(response => response.text())
            .then(contents => {
              console.log('Looking for old-school tease', contents)
              this.loading = false
              if (
                parser
                  .parseFromString(contents, 'text/html')
                  .getElementById('tease_title')
              ) {
                this.error = 'Sorry, classic teases are not supported.'
              } else {
                throw e
              }
            })
            .catch(() => {
              this.error =
                'Does not appear to be a valid EOS tease (Invalid JSON)'
              console.error('JSON response error', e)
              this.loading = false
            })
        })
    },
    getRemoteScriptName(uri, script) {
      fetch(
        encodeForCorsProxy(
          `https://milovana.com/webteases/showtease.php`,
          `id=${uri}&${FIX_POLLUTION}` +
            (this.previewMode
              ? '&ncpreview=' + this.previewMode
              : '&cacheable&_nc=' + Math.floor(Date.now() / 1000 / 60))
        ),
        { headers: corsProxyHeaders() }
      )
        .then(response => response.text())
        .then(contents => {
          this.loading = false
          // console.log('HTML response', contents)
          try {
            const doc = parser
              .parseFromString(contents, 'text/html')
              .getElementsByTagName('body')[0]
            this.title = doc.getAttribute('data-title')
            this.author = doc.getAttribute('data-author')
            this.authorId = doc.getAttribute('data-author-id')
            this.teaseId = doc.getAttribute('data-tease-id')
            this.teaseKey = doc.getAttribute('data-key')
            if (this.title) document.title = this.title
            this.script = script
          } catch (e) {
            console.error('Error parseing EOS HTML', e)
            this.title = 'Error Getting Title'
          }
        })
        .catch(e => {
          this.error = 'Error loading tease HTML from Milovana'
          console.error('HTML response error', e)
          this.loading = false
        })
    },
  },
}
</script>
<style>
.oeos-title b {
  display: inline-block;
  margin-right: 5px;
}
.oeos-title span {
  opacity: 0.8;
}
.oeos-title svg {
  margin-bottom: -5px;
}
.oeos-title svg + b {
  margin-left: 5px;
}
.theme--dark.v-application {
  background-color: var(--v-background-base, #121212) !important;
}
.theme--light.v-application {
  background-color: var(--v-background-base, white) !important;
}

/* ===== TEASE 1 REBUILD: INTRO / MENU / WARNING ===== */
/* Dedicated viewport layer. Do not rely on v-app/content dimensions for UI screens. */
.tease-ui-fixed-bg {
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-position: center center !important;
  background-repeat: no-repeat !important;
  background-size: cover !important;
  background-color: #000 !important;
  pointer-events: none !important;
  z-index: 0 !important;
}
.has-ui-background > .v-application--wrap {
  position: relative !important;
  z-index: 1 !important;
}
/* The EOS menu/warning image is only data; the fixed layer above renders it. */
.first-menu-bg .v-main img,
.first-special-start-screen .v-main img {
  display: none !important;
}

/* The UI photo now lives directly on <v-app>. */
.v-application.has-ui-background {
  background-color: #000 !important;
  background-size: cover !important;
  background-position: center center !important;
  background-repeat: no-repeat !important;
}

.v-application.has-ui-background > .v-application--wrap,
.has-ui-background .v-main,
.has-ui-background .v-main__wrap {
  background: transparent !important;
  background-color: transparent !important;
}


/* Menu/warning must expose the app background through every player shell. */
.first-menu-bg .oeos-outer,
.first-menu-bg .oeos-main,
.first-menu-bg open-eos-player,
.first-special-start-screen .oeos-outer,
.first-special-start-screen .oeos-main,
.first-special-start-screen open-eos-player {
  background: transparent !important;
  background-color: transparent !important;
}

/* Transparent glass UI. Gameplay GREEN/RED styling is untouched. */
.first-menu-bg .oeos-say-bubble,
.first-menu-bg .v-card.oeos-say-bubble,
.first-special-start-screen .oeos-say-bubble,
.first-special-start-screen .v-card.oeos-say-bubble,
.first-menu-bg .v-card,
.first-special-start-screen .v-card {
  background: rgba(0, 0, 0, .22) !important;
  background-color: rgba(0, 0, 0, .22) !important;
  backdrop-filter: blur(4px) !important;
  -webkit-backdrop-filter: blur(4px) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, .16) !important;
}
.first-menu-bg .v-btn,
.first-menu-bg button,
.first-special-start-screen .v-btn,
.first-special-start-screen button {
  background: rgba(0, 0, 0, .30) !important;
  background-color: rgba(0, 0, 0, .30) !important;
  border: 1px solid rgba(255, 255, 255, .28) !important;
  color: #fff !important;
  box-shadow: none !important;
  backdrop-filter: blur(3px) !important;
  -webkit-backdrop-filter: blur(3px) !important;
}
.first-menu-bg .v-btn::before,
.first-special-start-screen .v-btn::before {
  background-color: transparent !important;
}
.first-menu-bg .v-btn:hover,
.first-menu-bg button:hover,
.first-special-start-screen .v-btn:hover,
.first-special-start-screen button:hover {
  background: rgba(255, 255, 255, .16) !important;
  background-color: rgba(255, 255, 255, .16) !important;
}

/* Gameplay media: whole image visible, no crop. */
.first-gameplay-full-image {
  background-color: #000 !important;
}
.first-gameplay-full-image .v-main,
.first-gameplay-full-image .v-main__wrap,
.first-gameplay-full-image open-eos-player,
.first-gameplay-full-image open-eos-player > * {
  min-height: 100vh !important;
}
.first-gameplay-full-image img {
  object-fit: contain !important;
  object-position: center center !important;
}
.first-gameplay-full-image [style*="background-image"] {
  background-size: contain !important;
  background-position: center center !important;
  background-repeat: no-repeat !important;
}

</style>
